/**
 * [2026-05-29] Modal d'alerte « identifiants OpenPaye expirés ».
 *
 * V1 : déclenchement codé en dur sur la liste TARGET_EMAILS (ciblage manuel).
 * V2 (à venir) : déclenchement automatique basé sur un flag retourné par le
 * backend (par ex. user.domainCredentialsStatus === 'invalid'), pour que ce
 * popup s'affiche à tout cabinet dont les credentials OpenPaye ne sont plus
 * reconnus, sans intervention manuelle.
 *
 * Le composant ne s'affiche qu'une fois par session (sessionStorage), pour
 * éviter de spammer l'utilisateur à chaque changement de page.
 */
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ROUTES } from '@/constants/app';
import { useAuthStore } from '@/store/auth.store';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// V1 : liste codée en dur. À remplacer par une condition basée sur un flag user en V2.
const TARGET_EMAILS = ['emmanuelle@faisolutions.fr'];

const DISMISS_KEY = 'openpaye-credentials-alert-dismissed';

export function OpenPayeCredentialsAlertModal() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const email = user?.email?.toLowerCase();
    if (!email) return;
    if (!TARGET_EMAILS.includes(email)) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    setIsOpen(true);
  }, [user?.email]);

  const handleClose = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setIsOpen(false);
  };

  const handleGoToSettings = () => {
    handleClose();
    navigate(ROUTES.DOMAINS);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Identifiants OpenPaye à mettre à jour"
      size="md"
    >
      <div className="space-y-4">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning-100">
            <AlertTriangle className="h-10 w-10 text-warning-600" />
          </div>
        </div>

        <div className="space-y-2 text-center">
          <p className="text-sm leading-relaxed text-neutral-700">
            Nous avons détecté que les identifiants OpenPaye de votre cabinet
            ne sont plus reconnus par OpenPaye (mot de passe peut-être changé
            récemment ?).
          </p>
          <p className="text-sm leading-relaxed text-neutral-700">
            Pour que les bulletins de vos salariés continuent de se déposer
            automatiquement dans leur coffre-fort, merci de mettre à jour
            votre mot de passe OpenPaye dans les paramètres de votre domaine.
          </p>
        </div>

        <div className="flex justify-center gap-3 pt-2">
          <Button variant="outline" onClick={handleClose}>
            Plus tard
          </Button>
          <Button onClick={handleGoToSettings}>
            Aller dans les paramètres
          </Button>
        </div>
      </div>
    </Modal>
  );
}
